import React, { useState } from 'react';
import { X, Loader2, Send } from 'lucide-react';
import { notifyError, notifySuccess } from '../lib/notify';

const API = process.env.REACT_APP_BACKEND_URL || '';

/**
 * "Message the submitter" modal. Posts to /api/housing/listings/:id/contact
 * which routes the message into the recipient's Mundalo inbox.
 */
const HousingContactModal = ({ open, onClose, listing }) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  if (!open || !listing) return null;

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = message.trim();
    if (trimmed.length < 4) {
      notifyError('Add a short message before sending.');
      return;
    }
    setSending(true);
    try {
      const r = await fetch(
        `${API}/api/housing/listings/${encodeURIComponent(listing.id)}/contact`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ message_text: trimmed }),
        },
      );
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        notifyError(d.detail || 'Could not send the message.');
        setSending(false);
        return;
      }
      notifySuccess('Message sent. They will see it in their Mundalo inbox.');
      setMessage('');
      onClose();
    } catch {
      notifyError('Network error. Please try again.');
    }
    setSending(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#0A1F2E]/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      data-testid="housing-contact-modal"
      onClick={onClose}
    >
      <div
        className="bg-[#F9F6EF] border border-[#EFE6CF] rounded-2xl max-w-md w-full shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#EFE6CF]">
          <div>
            <p className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#1A5C6E]">
              Send a message
            </p>
            <h2 className="font-display italic text-2xl text-[#0A1F2E] leading-tight mt-1">
              "{listing.title}"
            </h2>
            <p className="text-[12px] text-[#0A1F2E]/55 mt-1">
              {listing.submitted_by_name} · {listing.city}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-[#0A1F2E]/55 hover:text-[#0A1F2E] transition"
            data-testid="housing-contact-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <p className="text-sm text-[#0A1F2E]/70">
            They will see your message in their Mundalo inbox and can reply
            directly to you.
          </p>
          <div>
            <label className="font-mono uppercase text-[10px] tracking-[0.15em] text-[#0A1F2E]/60 mb-1.5 block">
              Your message
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={5}
              placeholder="Hi! I'm interested in your listing - is it still available? When can we tour?"
              className="w-full bg-white border border-[#EFE6CF] text-[#0A1F2E] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#2CA8A0] focus:ring-2 focus:ring-[#2CA8A0]/25 transition"
              data-testid="housing-contact-textarea"
              maxLength={2000}
              autoFocus
            />
            <p className="text-[10px] text-[#0A1F2E]/40 mt-1">
              {message.length} / 2000
            </p>
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-[#EFE6CF]">
            <button
              type="button"
              onClick={onClose}
              className="font-mono uppercase tracking-[0.15em] text-[10px] text-[#0A1F2E]/60 hover:text-[#0A1F2E] px-4 py-2 transition"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              className="inline-flex items-center gap-2 bg-[#0A1F2E] hover:bg-[#163448] text-white rounded-full px-5 py-2.5 font-mono uppercase text-[11px] tracking-[0.15em] transition disabled:opacity-50"
              data-testid="housing-contact-send"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Send message
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default HousingContactModal;
